/**
 * Pure helpers for SearchableDropdown so they can be unit-tested without
 * rendering the component.
 */
import type { DropdownItem } from './SearchableDropdown';

/** True when the query matches either the item's value or its label (case-insensitive). */
export function matches(item: DropdownItem, query: string): boolean {
  if (!query) return true;
  const q = query.toUpperCase();
  return (
    item.value.toUpperCase().includes(q) ||
    item.label.toUpperCase().includes(q)
  );
}

/** Split an item list into highlighted / other groups, preserving input order. */
export function partitionItems(items: readonly DropdownItem[]): {
  highlighted: DropdownItem[];
  other: DropdownItem[];
} {
  const highlighted: DropdownItem[] = [];
  const other: DropdownItem[] = [];
  for (const item of items) {
    if (item.highlighted) highlighted.push(item);
    else other.push(item);
  }
  return { highlighted, other };
}

/**
 * Decide whether the "Other" group should auto-expand.
 *
 * We expand when the user has typed something that matches only "other"
 * items — otherwise they'd see "No matches in active list" and wonder why
 * their search found nothing, even though the full list has a hit.
 */
export function shouldAutoExpandOther(
  query: string,
  filteredHighlighted: readonly DropdownItem[],
  filteredOther: readonly DropdownItem[],
): boolean {
  return (
    query.length > 0 &&
    filteredHighlighted.length === 0 &&
    filteredOther.length > 0
  );
}
